import {
  AccessError,
  ApiError,
  AuthError,
  UnexpectedBehaviour,
} from '@u1/transport-core';
import { ActionType, initialFilters, tableFiltersReducer } from './filters';
import { Cross } from '../Cross';
import { IAppConfig, IUser } from '../../../types';
import {
  IPaging,
  ISort,
  ITableColumn,
  ITableColumnProps,
  Table,
  TableCell,
} from '@u1/aligntech-uikit';
import { getCanaryUsersRequest } from '../../../clients';
import { useGlobalContext } from '../../context';
import React, { useCallback, useRef } from 'react';
import useStyles from './UsersTable.styles';

interface IUserTableProps {
  appConfig: IAppConfig;
  users: IUser[];
  onUsersChangeHandler: (users: IUser[]) => void;
  onDeleteCanaryUserHandler: (userToDelete: IUser) => void;
  isUserRemovalAvailable: boolean;
}

export const UsersTable: React.FC<IUserTableProps> = ({
  appConfig,
  users,
  onUsersChangeHandler,
  onDeleteCanaryUserHandler,
  isUserRemovalAvailable,
}) => {
  const classes = useStyles();
  const { canary } = useGlobalContext();
  const [total, setTotal] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const { current: onUsersChangeHandlerStable } = useRef(onUsersChangeHandler);
  const [isInitialLoadFinished, setIsInitialLoadFinished] =
    React.useState(false);

  const onUsersChange = useCallback(
    (users: IUser[]) => {
      onUsersChangeHandlerStable(users);
    },
    [onUsersChangeHandlerStable],
  );

  const [tableFilters, dispatchFilter] = React.useReducer(
    tableFiltersReducer,
    initialFilters,
  );

  const handlePaging = (page: IPaging) => {
    dispatchFilter({ type: ActionType.SET_PAGE, paging: page });
  };
  const handleSort = (sort: ISort) => {
    dispatchFilter({ type: ActionType.SET_SORT, sorting: sort });
  };

  const handleUserLoginChange = (value: string) => {
    dispatchFilter({
      type: ActionType.SET_USER_LOGIN,
      userLogin: value,
    });
  };

  const handleDisplayNameChange = (value: string) => {
    dispatchFilter({
      type: ActionType.SET_DISPLAY_NAME,
      displayName: value,
    });
  };

  const handleResetFilters = () => {
    dispatchFilter({
      type: ActionType.RESET,
    });
  };

  const columnTemplate = ({
    item,
    column,
  }: ITableColumnProps<IUser>): React.ReactElement => (
    // @ts-ignore
    <TableCell width={column.width}>
      {isUserRemovalAvailable && (
        <Cross user={item} onClick={() => onDeleteCanaryUserHandler(item)} />
      )}
    </TableCell>
  );

  const columns: Array<ITableColumn<IUser>> = [
    {
      apiProperty: 'userLogin',
      label: 'Login',
      filter: 'input' as const,
      filterOnChange: handleUserLoginChange,
      delayChange: 300,
      sort: true,
      clearable: true,
    },
    {
      apiProperty: 'displayName',
      label: 'Name',
      filter: 'input' as const,
      filterOnChange: handleDisplayNameChange,
      delayChange: 300,
      sort: true,
      clearable: true,
    },
    {
      apiProperty: 'actions',
      label: 'Reset',
      filter: 'button' as const,
      handleClick: handleResetFilters,
    },
    ...(isUserRemovalAvailable
      ? [
          {
            apiProperty: 'additional',
            label: 'Delete user',
            columnTemplate: columnTemplate,
          },
        ]
      : []),
  ];

  React.useEffect(() => {
    setIsLoading(true);
    const finalFilterAndSortParams: Record<string, string | number> = {
      page: tableFilters.paging.page - 1,
      size: tableFilters.paging.size,
      sort: `${tableFilters.sorting.sortBy},${tableFilters.sorting.sortDirection}`,
    };

    for (const [key, value] of Object.entries(tableFilters) as Array<
      [string, string]
    >) {
      if (key && value && key !== 'sorting' && key !== 'paging') {
        finalFilterAndSortParams[`${key}`] = value;
      }
    }

    getCanaryUsersRequest(appConfig, finalFilterAndSortParams)
      .then(response => {
        if (response) {
          if (response.users) {
            const canaryUsers = response.users.map(val => ({
              id: val.userLogin,
              userLogin: val.userLogin,
              displayName: val.displayName,
            }));
            onUsersChange(canaryUsers);
            setIsInitialLoadFinished(true);
            setTotal(response.total);
            setIsLoading(false);
          }
        }
      })
      .catch(
        (error: AccessError | UnexpectedBehaviour | AuthError | ApiError) => {
          setIsLoading(false);
          throw error;
        },
      );
  }, [tableFilters, appConfig, onUsersChange, canary]);

  const [endColumns, setEndColumns] = React.useState<ITableColumn<IUser>[]>([]);

  React.useEffect(() => {
    columns &&
      setEndColumns(
        columns.map(api => {
          if (api.apiProperty === 'userLogin') {
            api.filterValue = tableFilters.userLogin;
          }
          if (api.apiProperty === 'displayName') {
            api.filterValue = tableFilters.displayName;
          }
          return api;
        }),
      );
    // eslint-disable-next-line
  }, [tableFilters, isUserRemovalAvailable]);

  return (
    <div className={classes.root}>
      <Table
        isInitialLoading={!isInitialLoadFinished}
        isLoading={isLoading}
        items={users}
        columns={endColumns}
        sorting={tableFilters.sorting}
        paging={tableFilters.paging}
        showPagination={true}
        total={total}
        emptyText="No items in the list"
        rowBgHover={true}
        handlePaging={handlePaging}
        handleSorting={handleSort}
      />
    </div>
  );
};
